/**
 * A TEXT OR QUOTE BLOCK AS A READER SEES IT - headings, lists, bold, links - for the read view of a
 * piece the author cannot change (./ArticleReadView.tsx; the owner's 2026-10-05 "locked / another
 * view/UI"), and for a version read in the article's column (./HistoryPanel.tsx, design 24, T20).
 *
 * ★ THE SAME TREE THE EDITOR OPENS. The block's `body`, paragraph rows and marks go through
 * `editorDocFromText` - the converter the editor itself mounts (./TextBlockEditor.tsx) - so a
 * heading, a nested list or a link reads here exactly as it does in the editor, and nothing is
 * parsed twice two ways. The tree is drawn as plain elements: no editor is mounted, nothing is
 * focusable but a link, and nothing can be typed into.
 *
 * Only the starter kit's names the converter emits are drawn (`EDITOR_NODE`, `EDITOR_MARK_OF`); a
 * node or mark it does not know is drawn as its text, never dropped. A link opens in a new tab and
 * is only ever one the platform stored (`LINK_HREF_RE` held it on the way in).
 */
import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { EDITOR_NODE, editorDocFromText } from '../../../../../../shared/content-model/editor-json';
import type { EditorMark, EditorNode } from '../../../../../../shared/content-model/editor-json';
import type { QuoteBlock, TextBlock } from '../../../features/cabinet/api/editor-api';
import { membersOf } from './TextBlockEditor';
import classes from './read-view.module.css';

/** The classes the tree is drawn with - the article preview passes the app's or the website's. */
export interface ReadOnlyTextClasses {
  readonly text: string | undefined;
  readonly quote: string | undefined;
  /** The quote's words without its attribution - the website rules a bar beside these alone. */
  readonly quoteText?: string | undefined;
  readonly attribution: string | undefined;
}

const READ_VIEW_CLASSES: ReadOnlyTextClasses = {
  text: classes.text,
  quote: classes.quote,
  attribution: classes.attribution,
};

export function ReadOnlyText({
  block,
  classNames = READ_VIEW_CLASSES,
}: {
  readonly block: TextBlock | QuoteBlock;
  readonly classNames?: ReadOnlyTextClasses;
}) {
  const members = membersOf(block);
  const doc = editorDocFromText(block.kind, members.body, members.paragraphs, members.marks);
  const content = <>{(doc.content ?? []).map((node, index) => drawNode(node, String(index)))}</>;
  return block.kind === 'quote' ? (
    <blockquote className={classNames.quote}>
      <div className={classNames.quoteText}>{content}</div>
      {block.quoteAttribution === null || block.quoteAttribution === undefined ? null : (
        <footer className={classNames.attribution}>— {block.quoteAttribution}</footer>
      )}
    </blockquote>
  ) : (
    <div className={classNames.text}>{content}</div>
  );
}

function drawNode(node: EditorNode, key: string): ReactNode {
  const children = (node.content ?? []).map((child, index) =>
    drawNode(child, `${key}.${String(index)}`),
  );
  switch (node.type) {
    case EDITOR_NODE.paragraph:
      return <p key={key}>{children}</p>;
    case EDITOR_NODE.heading: {
      const level = node.attrs?.['level'];
      // The model's h1 is the piece's own section heading, under the title: one step down.
      const depth = typeof level === 'number' && level >= 1 && level <= 4 ? level + 1 : 2;
      const Heading = `h${String(depth)}` as 'h2' | 'h3' | 'h4' | 'h5';
      return <Heading key={key}>{children}</Heading>;
    }
    case EDITOR_NODE.bulletList:
      return <ul key={key}>{children}</ul>;
    case EDITOR_NODE.orderedList:
      return <ol key={key}>{children}</ol>;
    case EDITOR_NODE.listItem:
      return <li key={key}>{children}</li>;
    case EDITOR_NODE.hardBreak:
      return <br key={key} />;
    case EDITOR_NODE.text:
      return <Fragment key={key}>{withMarks(node.text ?? '', node.marks ?? [])}</Fragment>;
    default:
      return <Fragment key={key}>{children}</Fragment>;
  }
}

/** The text inside its marks, the first mark outermost. */
function withMarks(text: string, marks: readonly EditorMark[]): ReactNode {
  return marks.reduceRight<ReactNode>((inner, mark) => {
    switch (mark.type) {
      case 'bold':
        return <strong>{inner}</strong>;
      case 'italic':
        return <em>{inner}</em>;
      case 'underline':
        return <u>{inner}</u>;
      case 'strike':
        return <s>{inner}</s>;
      case 'code':
        return <code>{inner}</code>;
      case 'link': {
        const href = mark.attrs?.['href'];
        return typeof href === 'string' ? (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {inner}
          </a>
        ) : (
          inner
        );
      }
      default:
        return inner;
    }
  }, text);
}
